import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import CustomSearchDropdown from "../../CommonComponents/CustomSearchDropdown";
import {
  GET_INVENTORY,
  GET_USABLE_INVENTORY_CATEGORIES,
} from "./../../AdminInventoryDashboard/graphql";

/**
 * Props:
 *  - employeeData?: { id?: string, fullName?: string }
 *  - defaultMode?: 'inventory' | 'category'
 *  - onSubmit: (payload) => void
 *      payload = {
 *        mode: 'inventory',
 *        rows: [{ inventoryId, inventoryName }]
 *      }
 *      OR
 *      {
 *        mode: 'category',
 *        rows: [{ categoryId, categoryName, usableQuantity, assignQuantity }]
 *      }
 *  - onCancel?: () => void
 */
const AddInventoryForm = ({
  employeeData,
  defaultMode = "inventory",
  onSubmit,
  onCancel,
}) => {
  // 🔹 Global mode like AdminInventoryDashboard: by inventory OR by category
  const [mode, setMode] = useState(defaultMode); // 'inventory' | 'category'
  const [inventorySearchTerm, setInventorySearchTerm] = useState("");
  const [inventorySearchResults, setInventorySearchResults] = useState([]);

  // 🔹 Form rows (for current mode)
  const [rows, setRows] = useState([
    {
      inventoryId: "",
      inventoryName: "",
      categoryId: "",
      categoryName: "",
      usableQuantity: null,
      assignQuantity: "",
    },
  ]);

  // =========================
  //  DATA SOURCES (GraphQL)
  // =========================

  // Inventory list for "By Inventory"

  const {
    data: inventoryData,
    loading: invLoading,
    error: invError,
  } = useQuery(GET_INVENTORY, {
    variables: {
      data: {
        search: "",
        status: "UNASSIGNED", // adjust if needed
        onlyUsable: true,
        employeeId: null,
      },
    },
    fetchPolicy: "network-only",
  });

  const inventoryOptions =
    inventoryData?.getInventory?.inventoryList?.map((inv) => ({
      id: inv.id,
      name: inv.name,
      description: inv.description,
    })) || [];

  // Category list for "By Category" – reuse ADMIN_INVENTORY_DASHBOARD
  const {
    data: dashData,
    loading: catLoading,
    error: catError,
    refetch,
  } = useQuery(GET_USABLE_INVENTORY_CATEGORIES, {
    fetchPolicy: "network-only",
  });

  const categoryOptions =
    dashData?.getUsableInventoryCategory?.categories?.map((cat) => ({
      id: cat.id,
      name: cat.name,
      usableQuantity: cat.usableQuantity,
    })) || [];

  const getCategoryById = (id) =>
    categoryOptions.find((c) => String(c.id) === String(id));

  // =========================
  //  ROW HELPERS
  // =========================

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      {
        inventoryId: "",
        inventoryName: "",
        categoryId: "",
        categoryName: "",
        usableQuantity: null,
        assignQuantity: "",
      },
    ]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  };

  // reset rows when mode changes
  const handleModeChange = (nextMode) => {
    if (nextMode === mode) return;
    setMode(nextMode);
    setRows([
      {
        inventoryId: "",
        inventoryName: "",
        categoryId: "",
        categoryName: "",
        usableQuantity: null,
        assignQuantity: "",
      },
    ]);
  };

  // =========================
  //  SUBMIT
  // =========================

  const handleSubmit = () => {
    if (typeof onSubmit !== "function") return;

    if (mode === "inventory") {
      const cleanRows = rows
        .filter((r) => r.inventoryId)
        .map((r) => r.inventoryId);

      if (!cleanRows.length) {
        alert("Select at least one inventory item.");
        return;
      }
      console.log("Submitting inventory rows:", cleanRows);

      onSubmit({
        inventoryIds: cleanRows,
        employeeId: employeeData?.id || null,
      });
      return;
    }

    // mode === 'category'
    const cleanRows = rows
      .filter((r) => r.categoryId && Number(r.assignQuantity) > 0)
      .map((r) => ({
        categoryId: r.categoryId,

        quantity: Number(r.assignQuantity),
      }));

    if (!cleanRows.length) {
      alert("Select at least one category with a valid quantity.");
      return;
    }

    // Optional: validation against usableQuantity
    for (const row of cleanRows) {
      if (row.assignQuantity > row.usableQuantity) {
        alert(
          `You cannot assign more than usable quantity (${row.usableQuantity}) for category ${row.categoryName}.`
        );
        return;
      }
    }
    console.log("Submitting category rows:", cleanRows);

    onSubmit({
      employeeId: employeeData?.id || null,
      categoryAssignments: cleanRows,
    });
  };

  // =========================
  //  RENDER
  // =========================

  const isLoading = invLoading || catLoading;

  return (
    <div className="w-full max-w-3xl mx-auto bg-gray-100 rounded-2xl shadow-lg border border-gray-200 p-5 sm:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-gray-900 text-lg font-semibold">Assign Inventory</h2>
          <p className="text-xs text-gray-500 mt-1">
            {mode === "inventory"
              ? "Assign specific inventory items"
              : "Assign by category with quantity"}
            {employeeData?.fullName && (
              <>
                {" "}
                to{" "}
                <span className="text-[#7F58E6] font-medium">
                  {employeeData.fullName}
                </span>
              </>
            )}
            .
          </p>
        </div>

        {/* Global Mode Toggle */}
        <div className="flex items-center bg-white rounded-full p-1 text-xs">
          <button
            type="button"
            onClick={() => handleModeChange("inventory")}
            className={`px-3 py-1 rounded-full ${
              mode === "inventory" ? "bg-[#7F58E6] text-white" : "text-gray-600"
            }`}
          >
            By Inventory
          </button>
          <button
            type="button"
            onClick={() => handleModeChange("category")}
            className={`px-3 py-1 rounded-full ${
              mode === "category" ? "bg-[#7F58E6] text-white" : "text-gray-600"
            }`}
          >
            By Category
          </button>
        </div>
      </div>

      {isLoading && (
        <p className="text-xs text-gray-500 mb-3">Loading options…</p>
      )}
      {invError && (
        <p className="text-xs text-red-400 mb-2">
          Failed to load inventory options.
        </p>
      )}
      {catError && (
        <p className="text-xs text-red-400 mb-2">
          Failed to load category options.
        </p>
      )}

      {/* Rows */}
      <div className="space-y-3">
        {rows.map((row, index) => {
          const selectedCategory = row.categoryId
            ? getCategoryById(row.categoryId)
            : null;
          const usableQty =
            row.usableQuantity ??
            selectedCategory?.usableQuantity ??
            selectedCategory?.usable_quantity;

          return (
            <div
              key={index}
              className="flex flex-col gap-3 bg-white border border-gray-200 rounded-xl px-3 py-3"
            >
              {/* Top row: index + remove */}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center h-8 w-8 rounded-full bg-[#7F58E6] text-white text-xs font-bold shrink-0">
                    {index + 1}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveRow(index)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-red-500 text-red-400 text-xs hover:bg-red-500/10 transition-colors"
                    >
                      <span className="text-sm">✕</span>
                      <span>Remove</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Body content based on global mode */}
              {mode === "inventory" ? (
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs text-gray-500 mb-1">
                      Inventory
                    </label>
                    <CustomSearchDropdown
                      className={`px-3 py-2 bg-white border border-gray-300 rounded-lg outline-none text-sm w-full ${
                        !row.inventoryId ? "text-[#9A9A9A]" : "text-gray-900"
                      }`}
                      value={row.inventoryId}
                      name="inventoryId"
                      placeholder="Select inventory"
                      options={inventoryOptions}
                      optionValueKey="id"
                      optionLabelKey="name"
                      onChange={(valOrEvent) => {
                        // if behaves like <select>
                        if (valOrEvent?.target) {
                          const { value } = valOrEvent.target;
                          const opt = inventoryOptions.find(
                            (o) => String(o.id) === String(value)
                          );
                          updateRow(index, {
                            inventoryId: value,
                            inventoryName: opt?.name || "",
                          });
                          return;
                        }

                        // otherwise treat as value or option
                        const selected =
                          typeof valOrEvent === "object" && valOrEvent !== null
                            ? valOrEvent
                            : inventoryOptions.find(
                                (o) => String(o.id) === String(valOrEvent)
                              );

                        if (!selected) {
                          updateRow(index, {
                            inventoryId: "",
                            inventoryName: "",
                          });
                          return;
                        }

                        updateRow(index, {
                          inventoryId: selected.id,
                          inventoryName: selected.name,
                        });
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Category dropdown */}
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs text-gray-500 mb-1">
                      Inventory Category
                    </label>
                    <CustomSearchDropdown
                      className={`px-3 py-2 bg-white border border-gray-300 rounded-lg outline-none text-sm w-full ${
                        !row.categoryId ? "text-[#9A9A9A]" : "text-gray-900"
                      }`}
                      value={row.categoryId}
                      name="categoryId"
                      placeholder="Select category"
                      options={categoryOptions}
                      optionValueKey="id"
                      optionLabelKey="name"
                      onChange={(e) => {
                        if (e?.target) {
                          const { value } = e.target;
                          const cat = getCategoryById(value);
                          updateRow(index, {
                            categoryId: value,
                            categoryName: cat?.name || "",
                            usableQuantity: cat?.usableQuantity ?? null,
                          });
                        } else {
                          const val = e;
                          const cat = getCategoryById(val);
                          updateRow(index, {
                            categoryId: val,
                            categoryName: cat?.name || "",
                            usableQuantity: cat?.usableQuantity ?? null,
                          });
                        }
                      }}
                    />
                  </div>

                  {/* Usable quantity */}
                  <div className="sm:w-40">
                    <label className="block text-xs text-gray-500 mb-1">
                      Usable Qty
                    </label>
                    <div className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-[#7F58E6] flex items-center justify-between">
                      <span className="text-gray-500 text-xs mr-1">
                        Available
                      </span>
                      <span className="font-semibold">{usableQty ?? "-"}</span>
                    </div>
                  </div>

                  {/* Assign quantity */}
                  <div className="sm:w-40">
                    <label className="block text-xs text-gray-500 mb-1">
                      Assign Qty
                    </label>
                    <input
                      type="number"
                      min={0}
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg outline-none text-sm text-gray-900 w-full"
                      value={
                        row.assignQuantity === undefined ||
                        row.assignQuantity === null
                          ? ""
                          : row.assignQuantity
                      }
                      onChange={(e) =>
                        updateRow(index, {
                          assignQuantity: e.target.value
                            ? Number(e.target.value)
                            : "",
                        })
                      }
                      placeholder="0"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer actions */}
      <div className="mt-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button
          type="button"
          onClick={handleAddRow}
          className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-dashed border-[#7F58E6] text-[#7F58E6] text-sm font-medium hover:bg-[#2b210f] transition-colors"
        >
          <span className="mr-1 text-base">＋</span>
          Add another row
        </button>

        <div className="flex gap-2">
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex items-center justify-center px-4 py-2 rounded-lg border border-gray-500 text-gray-600 text-sm hover:bg-[#2a2a2a] transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={handleSubmit}
            className="inline-flex items-center justify-center px-6 py-2 bg-[#7F58E6] text-white font-semibold rounded-lg text-sm hover:bg-[#e0a30d] transition-colors"
          >
            Submit Inventory
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddInventoryForm;
